/**
 * KinsCircle Phase 6C — Read-only semantic memory indexer.
 *
 * STABILITY.md §13:
 *   - NEVER mutates any memory row.
 *   - NEVER writes embeddings to Supabase.
 *   - NEVER persists user content beyond what the user already saved.
 *   - The index lives ONLY in localStorage, capped at 2,500 memories
 *     and ~3MB of serialized cache, with LRU eviction.
 */

import {
  cosineSimilarity,
  generateMemoryEmbedding,
  rankSemanticMatches,
  clusterRelatedMemories,
  embedQuery,
  type EmbeddingVector,
  type MemoryCluster,
  type SemanticMemoryRow,
  type SemanticSearchResult,
} from './memoryEmbeddingAdapter';
import { isSemanticIndexEnabled } from './semanticFeatureFlags';

const INDEX_LS_KEY = 'kinscircle.semantic.index.v1';
const MAX_MEMORIES = 2500;
const MAX_BYTES = 3 * 1024 * 1024;

type IndexedEntry = {
  row: SemanticMemoryRow;
  vector: EmbeddingVector;
  /** Unix ms — last time this entry was touched (LRU eviction). */
  touchedAt: number;
};

type IndexFile = {
  version: 1;
  entries: IndexedEntry[];
  builtAt: number;
};

const emptyIndex = (): IndexFile => ({
  version: 1,
  entries: [],
  builtAt: 0,
});

const safeReadIndex = (): IndexFile => {
  if (typeof window === 'undefined' || !window.localStorage) return emptyIndex();
  try {
    const raw = window.localStorage.getItem(INDEX_LS_KEY);
    if (!raw) return emptyIndex();
    const parsed = JSON.parse(raw) as IndexFile;
    if (!parsed || parsed.version !== 1 || !Array.isArray(parsed.entries)) {
      return emptyIndex();
    }
    return parsed;
  } catch {
    return emptyIndex();
  }
};

const safeWriteIndex = (file: IndexFile): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    let serialized = JSON.stringify(file);
    if (serialized.length > MAX_BYTES) {
      // Evict oldest until under the byte cap.
      const sorted = [...file.entries].sort(
        (a, b) => a.touchedAt - b.touchedAt,
      );
      while (sorted.length && serialized.length > MAX_BYTES) {
        sorted.shift();
        serialized = JSON.stringify({
          version: 1,
          entries: sorted,
          builtAt: file.builtAt,
        });
      }
      file = { version: 1, entries: sorted, builtAt: file.builtAt };
    }
    window.localStorage.setItem(INDEX_LS_KEY, serialized);
  } catch {
    /* non-fatal */
  }
};

const evictIfFull = (entries: IndexedEntry[]): IndexedEntry[] => {
  if (entries.length <= MAX_MEMORIES) return entries;
  return [...entries]
    .sort((a, b) => b.touchedAt - a.touchedAt)
    .slice(0, MAX_MEMORIES);
};

/**
 * Build (or rebuild) the semantic index from a fresh batch of memories.
 * Replaces the existing index entirely. Safe no-op when the flag is OFF.
 */
export const buildSemanticMemoryIndex = (
  memories: SemanticMemoryRow[],
): { ok: true; indexed: number } | { ok: false; reason: string } => {
  if (!isSemanticIndexEnabled()) {
    return { ok: false, reason: 'disabled' };
  }
  const now = Date.now();
  const trimmed = memories.slice(0, MAX_MEMORIES);
  const entries: IndexedEntry[] = trimmed.map((row) => ({
    row,
    vector: generateMemoryEmbedding(row),
    touchedAt: now,
  }));
  safeWriteIndex({ version: 1, entries, builtAt: now });
  return { ok: true, indexed: entries.length };
};

/**
 * Idempotent single-memory upsert. If the row already exists in the
 * index it is replaced; otherwise it is appended (with LRU eviction).
 */
export const indexMemory = (memory: SemanticMemoryRow): void => {
  if (!isSemanticIndexEnabled()) return;
  const file = safeReadIndex();
  const now = Date.now();
  const next = file.entries.filter((e) => e.row.id !== memory.id);
  next.push({
    row: memory,
    vector: generateMemoryEmbedding(memory),
    touchedAt: now,
  });
  safeWriteIndex({
    version: 1,
    entries: evictIfFull(next),
    builtAt: file.builtAt || now,
  });
};

export const rebuildSemanticIndex = (
  memories: SemanticMemoryRow[],
): ReturnType<typeof buildSemanticMemoryIndex> =>
  buildSemanticMemoryIndex(memories);

/** Wipe the local cache. Cheap, instant, no server calls. */
export const clearSemanticIndex = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(INDEX_LS_KEY);
  } catch {
    /* non-fatal */
  }
};

export const readSemanticIndexStats = (): {
  indexed: number;
  builtAt: number;
  cacheBytes: number;
} => {
  const file = safeReadIndex();
  let cacheBytes = 0;
  try {
    cacheBytes =
      (typeof window !== 'undefined' &&
        window.localStorage?.getItem(INDEX_LS_KEY)?.length) ||
      0;
  } catch {
    cacheBytes = 0;
  }
  return {
    indexed: file.entries.length,
    builtAt: file.builtAt,
    cacheBytes,
  };
};

const touchEntries = (ids: Set<string>): void => {
  if (!ids.size) return;
  const file = safeReadIndex();
  const now = Date.now();
  let dirty = false;
  for (const e of file.entries) {
    if (ids.has(e.row.id)) {
      e.touchedAt = now;
      dirty = true;
    }
  }
  if (dirty) safeWriteIndex(file);
};

/**
 * Run a free-form semantic search against the local index.
 * Returns an empty array when the flag is OFF or the index is empty.
 */
export const searchSemanticMemories = (
  query: string,
  opts: { topK?: number; minScore?: number; taggedNames?: string[] } = {},
): { results: SemanticSearchResult[]; durationMs: number } => {
  if (!isSemanticIndexEnabled()) return { results: [], durationMs: 0 };
  const trimmed = query.trim();
  if (!trimmed) return { results: [], durationMs: 0 };

  const start =
    typeof performance !== 'undefined' ? performance.now() : Date.now();
  const file = safeReadIndex();
  if (!file.entries.length) return { results: [], durationMs: 0 };

  const qVec = embedQuery(trimmed, opts.taggedNames ?? []);
  const results = rankSemanticMatches(
    qVec,
    file.entries.map((e) => ({ row: e.row, vector: e.vector })),
    { topK: opts.topK ?? 12, minScore: opts.minScore ?? 0.05 },
  );
  const end =
    typeof performance !== 'undefined' ? performance.now() : Date.now();

  touchEntries(new Set(results.map((r) => r.memoryId)));
  return { results, durationMs: Math.max(0, Math.round(end - start)) };
};

/**
 * Find memories closest to a single anchor memory already in the index.
 * Useful for "Related Memories" drawers.
 */
export const findRelatedMemories = (
  memoryId: string,
  opts: { topK?: number; minScore?: number } = {},
): SemanticSearchResult[] => {
  if (!isSemanticIndexEnabled()) return [];
  const file = safeReadIndex();
  const anchor = file.entries.find((e) => e.row.id === memoryId);
  if (!anchor) return [];
  const others = file.entries.filter((e) => e.row.id !== memoryId);
  return rankSemanticMatches(
    anchor.vector,
    others.map((e) => ({ row: e.row, vector: e.vector })),
    { topK: opts.topK ?? 6, minScore: opts.minScore ?? 0.1 },
  );
};

/**
 * Timeline-proximity helper. Surfaces every indexed memory in which
 * `memberKey` (a normalized lowercase tag name) appears, ordered by year.
 */
export const findTimelineConnections = (
  memberKey: string,
): { row: SemanticMemoryRow; year: number | null }[] => {
  if (!isSemanticIndexEnabled()) return [];
  const file = safeReadIndex();
  const key = memberKey.trim().toLowerCase();
  if (!key) return [];
  return file.entries
    .filter((e) => e.vector.people.includes(key))
    .map((e) => ({ row: e.row, year: e.vector.year }))
    .sort((a, b) => {
      if (a.year && b.year) return a.year - b.year;
      if (a.year) return -1;
      if (b.year) return 1;
      return 0;
    });
};

/** Greedy clustering across the full local index. */
export const buildMemoryClusters = (opts: {
  minClusterSize?: number;
  threshold?: number;
} = {}): MemoryCluster[] => {
  if (!isSemanticIndexEnabled()) return [];
  const file = safeReadIndex();
  if (!file.entries.length) return [];
  return clusterRelatedMemories(
    file.entries.map((e) => ({ row: e.row, vector: e.vector })),
    opts,
  );
};

/** Read-only accessor used by Historian + Resurfacer engines. */
export const readIndexedMemories = (): {
  row: SemanticMemoryRow;
  vector: EmbeddingVector;
}[] => {
  if (!isSemanticIndexEnabled()) return [];
  const file = safeReadIndex();
  return file.entries.map((e) => ({ row: e.row, vector: e.vector }));
};

/** Pure utility re-export so callers don't need to dual-import. */
export { cosineSimilarity };
